import React, { Component } from 'react';
import { withRouter } from 'react-router-dom';
import { Auth } from 'aws-amplify';
import { connect } from 'react-redux';
import { cloneDeep } from 'lodash';
import withStyles from 'react-jss';
import { Translation } from 'react-i18next';
import i18n from 'i18next';
import classNames from 'classnames';
import {
  getAuthState,
  getTenantState,
  getTenantSSOFlag,
} from '../../store/selectors';
import {
  setUser,
  setAuthStatus,
  getCurrentUserId,
  getData,
  checkUser,
  getFavoritesByUser,
  setUserIPAddress,
} from '../../store/actions';
import me3Logo from '../../ASUPhotos/logos/me3_logo_black_NEW.png';
import { loginCopy } from '../../copy/loginCopy.json';
import CampusBackground from '../CampusBackground';
import IntroImage from '../IntroImage';
import PoweredBy from '../utility/PoweredBy';
import RouteBack from '../utility/RouteBack';
import eyeIcon from '../../ASUPhotos/icons/eyeIcon.png';
import eyeCrossIcon from '../../ASUPhotos/icons/eyeCrossIcon.png';
import validate from '../utility/CheckError';
import ErrorMessage from '../ErrorMessage';
import SSOLogin from './SSOLogin';
import { loginPage_GTM } from '../../gtm/datalayers/loginDataLayers.json';
import { login_GTM } from '../../gtm/datalayers/utilDataLayers.json';
import { fireGTM } from '../../gtm/gtmUtil';
import { getIPAddress } from '../../util/userIPAddressUtil';

require('../../ASUPhotos/backgrounds/campus2.png');

const me3Link = 'https://me3.careers';

const loginButtonStyles = {
  color: (props) => ({
    backgroundColor: props.tenant.primary,

    '&:hover': {
      border: `2px solid ${props.tenant.primary}`,
      color: `${props.tenant.primary}`,
    },
  }),
};

class LogIn extends Component {
  constructor(props) {
    super(props);
    this.state = {
      email: '',
      password: '',
      errors: {
        emailNotInUse: false,
        emailInvalid: false,
        emailEmpty: false,
        password: false,
      },
      passwordIsMasked: true,
    };
    this.togglePasswordMask = this.togglePasswordMask.bind(this);
  }

  get jwtToken() {
    return this.props.auth.jwtToken;
  }

  get tenant() {
    return this.props.tenant;
  }

  get tenantHasSSO() {
    return this.props.tenantSSOFlag;
  }

  async componentDidMount() {
    this._isMounted = true;
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  handleErrors = async (event) => {
    event.preventDefault();

    const errorsCopy = cloneDeep(this.state.errors);
    const stateCopy = cloneDeep(this.state);
    const emailCopy = cloneDeep(this.state.email);
    const errors = await validate('email', emailCopy, errorsCopy, stateCopy);
    if (this._isMounted) {
      this.setState(
        {
          errors,
        },
        () => this.checkEmailErrors()
      );
    }
  };

  checkEmailErrors() {
    const { emailNotInUse, emailInvalid, emailEmpty } = this.state.errors;

    if (!emailNotInUse && !emailInvalid && !emailEmpty) {
      this.handleSubmit();
    }
  }

  async handleSubmit() {
    try {
      const user = await Auth.signIn(this.state.email, this.state.password);
      const session = await Auth.currentSession();
      const userId = await this.props.getCurrentUserId(
        session.idToken.jwtToken
      );

      this.props.setAuthStatus({ authenticated: true, session });
      // Populate the store with a user's previously saved info
      await this.props.getData(userId, session.idToken.jwtToken);
      // Get the user IP address
      const userIPAddress = await getIPAddress();
      if (userIPAddress)
        this.props.setUserIPAddress(
          userId,
          session.idToken.jwtToken,
          userIPAddress
        );
      await this.props.getFavoritesByUser(userId);
      this.props.setUser(user);
      // This will only fire if a successful login per Amir's request
      fireGTM(login_GTM, 'login', 'login');
    } catch (error) {
      if (this._isMounted) {
        if (error.message === 'Pending sign-in attempt already in progress') {
          return;
        };
        // using prevState here causes a slowdown on the login process it looks like, but otherwise we get a linting error
        this.setState((prevState) => {
          const errorsCopy = { ...prevState.errors };

          errorsCopy.password = true;

          return { errors: errorsCopy };
        });
        console.error('error logging in: ', error);
      }
    }
  }

  onInputChange = (event) => {
    if (this._isMounted) {
      if (event.target.id === 'email') {
        this.setState({
          email: event.target.value.toLowerCase(),
          errors: {
            emailNotInUse: false,
            emailInvalid: false,
            emailEmpty: false,
            password: false,
          },
        });
      } else {
        this.setState({
          [event.target.id]: event.target.value,
          errors: {
            emailNotInUse: false,
            emailInvalid: false,
            emailEmpty: false,
            password: false,
          },
        });
      }
    }
  };

  togglePasswordMask = () => {
    if (this._isMounted) {
      this.setState((prevState) => ({
        passwordIsMasked: !prevState.passwordIsMasked,
      }));
    }
  };

  keyDownPasswordMask = (e) => {
    if (['Enter'].includes(e.key)) {
      this.togglePasswordMask();
    }
  };

  visitLink() {
    window.open(me3Link, '_blank');
  }

  checkSSOLogin = (primaryBackground) => {
    return (
      <>
        <SSOLogin
          primaryBackground={primaryBackground}
          tenantHasSSO={this.tenantHasSSO}
          className={this.props.classes.color}
        />
        {this.props.tenant.name !== 'Baobab' && (
          <h2 className="login-page-divider">
            <span>{i18n.t('or', { context: 'conjunction' })}</span>
          </h2>
        )}
      </>
    );
  };

  handleForgotPasswordClick = async () => {
    await fireGTM(loginPage_GTM.forgotPassword, 'forgot password?', 'login');

    this.props.history.push('password-reset');
  };

  render() {
    const { errors } = this.state;
    const { primary, secondary } = this.props.tenant;
    const { passwordIsMasked } = this.state;

    const primaryBackground = {
      backgroundColor: `${primary}`,
    };

    const primaryColor = {
      color: `${primary}`,
    };

    const secondaryBorder = {
      borderBottom: `5px solid ${secondary}`,
    };

    const buttonText = {
      color: `${primary}`,
      fontSize: '15px',
    };
    const primaryBorder = {
      borderTop: `5px solid ${this.props.tenant.primary}`,
    };

    return (
      <Translation>
        {(t) => (
          <CampusBackground secondaryColor={this.props.tenant.secondary}>
            <div className="login-page ">
              <div className="login-left" style={primaryBorder}>
                <div className="content">
                  <div className="header-container">
                    <RouteBack
                      color={this.props.tenant.primary}
                      route="/landing"
                      tenant={this.props.tenant}
                      section="login"
                      text="back"
                    />
                    <div
                      role="link"
                      className={classNames({
                        'me3-link': true,
                        'invisible-focus-indicator': true,
                      })}
                      onClick={this.visitLink}
                      onKeyDown={(e) => {
                        if (['Enter'].includes(e.key)) this.visitLink();
                      }}
                      tabIndex="0"
                    >
                      {/* <img
                        className="me3-logo-mobile"
                        src={me3Logo}
                        alt="me3"
                      /> */}
                      <picture>
                        <source
                          media="(max-width: 799px)"
                          srcSet={me3Logo}
                          width="480"
                          height="400"
                        />
                        <source
                          media="(min-width: 800px)"
                          srcSet={me3Logo}
                          width="800"
                          height="400"
                        />
                        <img
                          className="me3-logo-mobile"
                          src={me3Logo}
                          alt="me3"
                          width="800"
                          height="400"
                        />
                      </picture>
                    </div>
                    <div className="login-text-container">
                      {/* Some content on other pages needs to be conditionally rendered based on screen size */}
                      <h1 className="login-header-text">{t('Login')}</h1>
                      <h1 className="login-header-text-mobile">{t('Login')}</h1>
                      <div
                        className="header-text-yellow-bar"
                        style={secondaryBorder}
                      />
                    </div>
                    {this.checkSSOLogin(primaryBackground)}
                  </div>
                  {this.props.tenant.name !== 'Baobab' && (
                    <form className="login-form" onSubmit={this.handleErrors}>
                      <div className="login-fields-container">
                        <div className="input-container">
                          <div className="label-and-error">
                            <p style={primaryColor} className="login-text">
                              {t('Email')}
                            </p>
                            <span
                              style={{
                                display: errors.emailNotInUse ? '' : 'none',
                              }}
                            >
                              <ErrorMessage error={t('Email not found')} />
                            </span>
                            <span
                              style={{
                                display: errors.emailInvalid ? '' : 'none',
                              }}
                            >
                              <ErrorMessage error={t('Email invalid')} />
                            </span>
                            <span
                              style={{
                                display: errors.emailEmpty ? '' : 'none',
                              }}
                            >
                              <ErrorMessage error={t('Field required')} />
                            </span>
                          </div>
                          <input
                            type="text"
                            id="email"
                            className={
                              errors.emailNotInUse ||
                              errors.emailInvalid ||
                              errors.emailEmpty
                                ? 'error input'
                                : 'input'
                            }
                            aria-label="email"
                            placeholder="Email"
                            data-cy="email"
                            onChange={this.onInputChange}
                          />
                        </div>
                        <div className="input-container">
                          <div className="label-and-error">
                            <p style={primaryColor} className="login-text">
                              {t('Password')}
                            </p>
                            <span
                              style={{ display: errors.password ? '' : 'none' }}
                            >
                              <ErrorMessage error={t('Incorrect password')} />
                            </span>
                          </div>
                          <div className="login-password">
                            <input
                              className={
                                errors.password ? 'error input' : 'input'
                              }
                              type={passwordIsMasked ? 'password' : 'text'}
                              id="password"
                              aria-label="password"
                              placeholder="•••••••••"
                              value={this.state.password}
                              onChange={this.onInputChange}
                              data-cy="password"
                            />
                            <img
                              className="password-eye"
                              onClick={this.togglePasswordMask}
                              onKeyDown={this.keyDownPasswordMask}
                              src={passwordIsMasked ? eyeIcon : eyeCrossIcon}
                              alt="eye"
                              tabIndex="0"
                              role="button"
                              aria-pressed={!passwordIsMasked}
                            />
                          </div>
                        </div>
                      </div>
                      <div className="input-bottom">
                        <div className="login-button-container">
                          <button
                            className={[
                              'button--primary',
                              'login-button',
                              'primary-filled',
                              this.props.classes.color,
                            ].join(' ')}
                            id="submit"
                            type="submit"
                            style={primaryBackground}
                            data-cy="submit-top"
                          >
                            {t('Login')}
                          </button>
                        </div>
                        <button
                          type="button"
                          className="button--text"
                          id="forgotPassword"
                          data-cy="forgot-password"
                          style={buttonText}
                          onClick={this.handleForgotPasswordClick}
                        >
                          {t('Forgot password?')}
                        </button>
                      </div>
                    </form>
                  )}
                </div>
                <PoweredBy tenant={this.props.tenant} />
              </div>
            </div>
            <IntroImage showTutorial={false} />
          </CampusBackground>
        )}
      </Translation>
    );
  }
}

const mapStateToProps = (state) => {
  const tenant = getTenantState(state);
  const auth = getAuthState(state);
  const tenantSSOFlag = getTenantSSOFlag(state);

  return { auth, tenant, tenantSSOFlag };
};

const LogInWithRouter = withRouter(
  connect(mapStateToProps, {
    setAuthStatus,
    setUser,
    getCurrentUserId,
    getData,
    checkUser,
    getFavoritesByUser,
    setUserIPAddress,
  })(LogIn)
);

export default withStyles(loginButtonStyles)(LogInWithRouter);
